/**
 * Contracts Hook
 * Manages Contract CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export interface Contract {
  id: string;
  projectId: string;
  baseSpecId: string | null;
  title: string;
  version: string;
  description: string | null;
  apiContract: Record<string, unknown> | null;
  dataContract: Record<string, unknown> | null;
  slaTerms: Record<string, unknown> | null;
  status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  linkedUseCases?: Array<{ id: string; title: string }>;
}

// List contracts
export function useContracts(projectId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['contracts', projectId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/contracts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch contracts');
      const data = await response.json();
      return data.data as Contract[];
    },
    enabled: !!projectId && !!token,
  });
}

// Get single contract
export function useContract(projectId: string, contractId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['contract', projectId, contractId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/contracts/${contractId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch contract');
      const data = await response.json();
      return data.data as Contract;
    },
    enabled: !!projectId && !!contractId && !!token,
  });
}

// Create contract
export function useCreateContract(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<Contract>) => {
      const response = await fetch(`/api/projects/${projectId}/contracts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create contract');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] });
    },
  });
}

// Update contract
export function useUpdateContract(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ contractId, data }: { contractId: string; data: Partial<Contract> }) => {
      const response = await fetch(`/api/projects/${projectId}/contracts/${contractId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update contract');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['contract', projectId, variables.contractId] });
    },
  });
}

// Update contract status
export function useUpdateContractStatus(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ contractId, status }: { contractId: string; status: Contract['status'] }) => {
      const response = await fetch(`/api/projects/${projectId}/contracts/${contractId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update contract status');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['contract', projectId, variables.contractId] });
    },
  });
}

// Link use case
export function useLinkUseCase(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ contractId, useCaseId }: { contractId: string; useCaseId: string }) => {
      const response = await fetch(`/api/projects/${projectId}/contracts/${contractId}/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ useCaseId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to link use case');
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['contract', projectId, variables.contractId] });
    },
  });
}

// Delete contract
export function useDeleteContract(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (contractId: string) => {
      const response = await fetch(`/api/projects/${projectId}/contracts/${contractId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete contract');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] });
    },
  });
}
